import { useState } from 'react';

import { type QueryState } from '../queryState';

/**
 * Like a QueryState, but with an unchecked error case that may come
 * from any source.
 */
export type UncheckedQueryState<T> =
  | (QueryState<T> & { status: 'loading' | 'success' })
  | UncheckedQueryError;

export type UncheckedQueryError = { status: 'error'; error: unknown };

const areArraysEquals = (a: string[] = [], b: string[] = []): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const sortedB = [...b].sort();
  return [...a].sort().every((value, index) => value === sortedB[index]);
};

export function usePromiseToQuery<T>(
  promise: () => Promise<T>,
  { isEnabled }: { isEnabled: boolean } = { isEnabled: true },
  promiseParams: string[] = [],
): UncheckedQueryState<T> {
  const [state, setState] = useState<UncheckedQueryState<T>>();
  const [params, setParams] = useState<string[]>(promiseParams);
  if (!isEnabled) {
    return { status: 'loading' };
  }

  if (!state || !areArraysEquals(params, promiseParams)) {
    (async () => {
      try {
        setParams(promiseParams);
        setState({ status: 'success', data: await promise() });
      } catch (error) {
        setState({ status: 'error', error });
      }
    })();

    setState({ status: 'loading' });
    return { status: 'loading' };
  }
  return state;
}
